import { useState, useEffect } from "react";
import {
  getAllMaterials,
  deleteMaterialById,
  getMaterialById,
  updateMaterialById,
  addProjectMaterial,
  uploadMaterialExcel,
} from "../../../Services/materialService";
import toast from "react-hot-toast";

export const useMaterials = (projectId, limit = 10) => {
  const [materials, setMaterials] = useState([]);
  const [materialCount, setMaterialCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const fetchMaterials = async (customSearch = search) => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;
      const res = await getAllMaterials(projectId, { limit, offset, search: customSearch });
      if (res?.data?.results) {
        setMaterials(res.data.results);
        setMaterialCount(res.data.count);
      }
    } catch {
      toast.error("Failed to fetch materials");
    } finally {
      setLoading(false);
    }
  };

  const deleteMaterial = async (id) => {
    try {
      const response = await deleteMaterialById(projectId, id);
      if (response.status === 200) {
        toast.success("Material deleted");
        setMaterials((prev) => prev.filter((m) => m.id !== id));
      } else {
        toast.error("Failed to delete material");
      }
    } catch {
      toast.error("Failed to delete material");
    }
  };

  const addMaterial = async (data) => {
    try {
      const response = await addProjectMaterial(projectId, data);

      if (response.status === 201) {
        toast.success("Material added");
        fetchMaterials();
      } else if (response.status === 400) {
        // Safely get the error message
        const errorMsg =
          response.error?.errors?.non_field_errors?.[0] ||
          response.error?.message ||
          "Failed to add material";
        toast.error(errorMsg);
      } else {
        toast.error("Failed to add material");
      }
    } catch (error) {
      // Check if it's an Axios error with response
      const errorMsg =
        error.response?.data?.errors?.non_field_errors?.[0] ||
        error.response?.data?.message ||
        error.message ||
        "Something went wrong";
      toast.error(errorMsg);
    }
  };


  const updateMaterial = async (id, data) => {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      if (key === "file" && !data[key]) {
        return;
      }

      if (key === "material_delivery_date") {
        if (data[key]) {
          const date = new Date(data[key]);
          const formattedDate = date.toISOString().split("T")[0];
          formData.append(key, formattedDate);
        } else {
          formData.append(key, "");
        }
      } else {
        formData.append(key, data[key]);
      }
    });

    try {
      const response = await updateMaterialById(projectId, id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        toast.success("Material updated");
        fetchMaterials();
      } else if (response.status === 400) {
        toast.error(response.error?.error || "Failed to update material");
      } else {
        toast.error(response.error?.error);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update material");
    }
  };
  
  const uploadExcelFile = async (file) => {
    try {
      const response = await uploadMaterialExcel(file);
      if (response.status === 201) {
        toast.success("File uploaded");
        fetchMaterials();
      } else if(response.status === 400) {
        toast.error(response.error?.error);
      } else {
        toast.error(response.error?.error || "Failed to upload file");
      }
    } catch(error) {
      toast.error(error.message || "File upload failed");
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [page]);

  return {
    materials,
    setMaterials,
    materialCount,
    loading,
    page,
    setPage,
    fetchMaterials,
    deleteMaterial,
    addMaterial,
    updateMaterial,
    uploadExcelFile,
    totalPages: Math.ceil(materialCount / limit),
    //search material
    search,
    setSearch,
  };
};
